if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.handleToggleATC = this.handleToggleATC.bind(this);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        document.addEventListener('deliveryAddress:statusUpdated', this.handleToggleATC);


        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        document.removeEventListener('deliveryAddress:statusUpdated', this.handleToggleATC);
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);
          // FPS-625: a web-hidden variant (osprey.web_item=false) can still be reached via the
          // multi-option picker window (value.variant is unresolved at render, so the option
          // isn't pre-disabled). Treat it exactly like an unavailable selection so its pickup,
          // inventory, price and SKU are never exposed. The re-rendered buy button carries
          // data-web-hidden.
          const webHidden =
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.dataset.webHidden === 'true';

          this.pickupAvailability?.update(webHidden ? null : variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant || webHidden) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);
          const container = this.closest('quick-view-modal, .shopify-section')

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = container.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('selling-plan');
          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('locationStockStatus');
          updateSourceFromDestination('locationDeliveryConfig');
          // The per-variant pickup availability JSON was just swapped in above; refresh the
          // selected-store panel(s) synchronously so their stock status — and the Add-to-Cart
          // button state derived from it — track the new variant immediately instead of
          // lagging behind location-status.js's 100ms debounce (FPS-614).
          this.querySelectorAll('location-status').forEach((el) => el.refreshNow?.());
          // FPS-677: the delivery-address config (availableLocations for this variant) must
          // also refresh, or add-to-cart keeps validating against the previous variant's
          // enabled locations after a variant switch.
          this.querySelectorAll('location-delivery-status[data-type="main"]').forEach((el) => el.refreshNow?.());
          updateSourceFromDestination('stickyPrice');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const forceDisable = addButtonUpdated ? addButtonUpdated.getAttribute('data-force-disable') === 'true' : true;

          if (forceDisable) {
            // Variant is unavailable — disable immediately and keep it locked so the
            // pickup/delivery path can't re-enable a non-purchasable variant. Use the newly
            // rendered button's own label ("Unavailable" for a web-hidden osprey.web_item=false
            // variant vs "Sold out" for no stock) instead of always assuming sold-out.
            const forcedLabel = addButtonUpdated?.querySelector('.btn__text')?.textContent;
            this.toggleAddButton(true, forcedLabel || FoxTheme.variantStrings.soldOut);
            this.setForceDisable(true);
          } else {
            // Variant is available — clear any lock left by a previous invalid/sold-out
            // selection so the pickup/delivery re-evaluation below can enable it again.
            this.setForceDisable(false);
            // The pickup/delivery re-evaluation only toggles the disabled
            // state, never the label — so reset the label to the newly selected variant's
            // server-rendered text ourselves, otherwise a stale "Sold out"/"Unavailable" label
            // (or the wrong MAP vs. normal add-to-cart string) from a previous variant persists (FPS-614).
            const newLabel = addButtonUpdated?.querySelector('.btn__text')?.textContent;
            const deliveryOptions = this.querySelector('delivery-options');
            if (deliveryOptions && typeof deliveryOptions.dispatchSubmitButtonUpdate === 'function') {
              // delivery-options owns the enable/disable decision; set the label before it re-evaluates.
              if (newLabel != null) {
                const liveLabel = document
                  .getElementById(`product-form-${this.dataset.section}`)
                  ?.querySelector('[name="add"] > span');
                if (liveLabel) liveLabel.textContent = newLabel;
              }
              deliveryOptions.dispatchSubmitButtonUpdate();
            } else {
              // No delivery-options: enable directly, applying the variant's own label (e.g. the
              // MAP "Add to cart to view sale price" string) rather than the generic default.
              this.toggleAddButton(false, newLabel);
            }
          }

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      handleToggleATC(event) {
        const { available = false } = event.detail ?? {};
        if (!available) {
          this.toggleAddButton(true);
          return;
        }
        // Delegate to delivery-options so frequency + delivery method checks still apply
        const deliveryOptions = this.querySelector('delivery-options');
        if (deliveryOptions && typeof deliveryOptions.dispatchSubmitButtonUpdate === 'function') {
          deliveryOptions.dispatchSubmitButtonUpdate();
        } else {
          this.toggleAddButton(false);
        }
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          // Respect the hard lock: an invalid/non-purchasable selection (data-force-disable)
          // must not be re-enabled here — e.g. handleToggleATC's delivery-address "in stock"
          // path calls toggleAddButton(false) directly, bypassing ProductForm.updateSubmitButton's
          // own force-disable guard (FPS-614).
          if (addButton.getAttribute('data-force-disable') === 'true') return;
          // Same for the location gate: the resolved store holds none of this variant
          // (variant-location-stock.js), which is not a state this path knows how to clear.
          if (addButton.dataset.locationGated === 'true') return;
          addButton.removeAttribute('disabled');
          // Apply the caller-supplied label (e.g. a variant's own server-rendered text) when
          // given; otherwise fall back to the generic localized add-to-cart string.
          if (text) {
            addButtonText.textContent = text;
          } else {
            addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
          }
        }

        if (!modifyClass) return;
      }

      // Hard-lock / unlock the Add-to-Cart button via data-force-disable. When locked,
      // ProductForm.updateSubmitButton (and the pickup/delivery re-evaluation that feeds it)
      // refuses to re-enable the button. Used to make an invalid size/colour combination —
      // which has no real variant — genuinely unpurchasable instead of a clickable button
      // that reads "Unavailable" but still submits an empty variant id (FPS-614).
      setForceDisable(force) {
        const addButton = document
          .getElementById(`product-form-${this.dataset.section}`)
          ?.querySelector('[name="add"]');
        if (!addButton) return;
        if (force) {
          addButton.setAttribute('data-force-disable', 'true');
        } else {
          addButton.removeAttribute('data-force-disable');
        }
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        // No matching variant for the selected options — lock the button so a pickup/delivery
        // "in stock" signal can't re-enable a purchase that would fail with "Cannot find variant".
        this.setForceDisable(true);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
